import { ProductCatalog } from "@vp/hamilton-service-clients";
import { Logger } from "../../../logging/logger";
import {
  CloudinaryImage,
  ContentfulCloudinaryImageComponent,
  MerchandisingDetail,
} from "../../../types/ContentfulTypes";
// @ts-ignore
import VistaprintAuth from "@vp/auth";
import { getSelectionFromUrl } from "../../../utils/urlHelper";
import { ProductCatalogCompatibilityRequestModel } from "@vp/hamilton-service-clients/dist/src/models/ProductCatalog/ProductCatalogCompatibility";
import CartService from "../../../services/CartService";
import PageConfig from "../../../utils/PageConfig";
import Stopwatch from "../../../utils/Stopwatch";
import TrackingUtil from "../../../utils/tracking";
import { LowestPrice } from "@vp/hamilton-service-clients/dist/src/types/Pricing";
import { getProductCompletion } from "../../../services/ranch";
import { CloudinaryVideoMedia } from "../../../types/CloudinaryTypes";
import { getAction } from "../../../services/siteflow";
// @ts-ignore
import { ensureUserTestId } from "@vp/ab-test-cookie";
import { ProductBuilderOptions } from "../../../types/ProductBuilderTypes";
import { getDefaultImage } from "../../../utils/ProductBuilderHelper";

export interface AddToCartOptions {
  productKey: string;
  locale: string;
  country: string;
  tenant: string;
  quantity: number;
  productName: string;
  selections: Record<string, string>;
  imageUrl: string;
  productPageMerchandisingDetail?: MerchandisingDetail;
  vistacartTenant: string | undefined;
  initialSelections: Record<string, string>;
  mpvId: string;
  pageSection: string;
  prices: Record<number, LowestPrice>;
  version: number;
  isMobile: boolean;
  productBuilderOptions: ProductBuilderOptions;
  initialPrices: Record<string, LowestPrice> | undefined;
}

// Utils
const productCatalog = ProductCatalog;
const productCatalogConfig = new productCatalog.config(PageConfig.requestor);
const productCatalogServiceClient = new productCatalog.serviceClient(productCatalogConfig);

const addToCartOnClickHandler = async (
  e: React.MouseEvent<HTMLElement, MouseEvent>,
  addToCartOptions: AddToCartOptions,
  initialQuantity: number
) => {
  e.preventDefault();
  try {
    await addToCart(addToCartOptions, initialQuantity);
  } catch (err) {
    const error = err as any;
    Logger.instance.error("Cannot proceed from product page: Failed to add to cart", {
      errorStack: error.stack,
    });
  }
};

const addToCart = async (addTocartOptions: AddToCartOptions, initialQuantity: number) => {
  const {
    productKey,
    locale,
    country,
    quantity,
    productName,
    selections,
    imageUrl,
    productPageMerchandisingDetail,
    vistacartTenant,
    initialSelections,
    mpvId,
    version,
    prices,
    pageSection,
    tenant,
    isMobile,
    productBuilderOptions,
    initialPrices,
  } = addTocartOptions;
  const stopwatch = new Stopwatch();
  const authLibrary = new VistaprintAuth.WebAuth();
  const authorizationHeader = authLibrary.getAuthorizationHeader();
  const shopperId = authLibrary.getCanonicalId();

  const selectionsFromUrl = getSelectionFromUrl() || selections;

  // Product completion call to get default attributes to make it a fully configured product
  const productCompletionRequest = {
    productId: productKey,
    market: country,
    selectedOptions: selectionsFromUrl,
    version,
  };

  const completeProduct = await getProductCompletion(productCompletionRequest);
  if (!completeProduct) {
    Logger.instance.error("Failed to get default attributes. Failed to add to cart.");
    return;
  }

  // Check for option and quantity compatibility
  const compatibilityRequest: ProductCatalogCompatibilityRequestModel = {
    productKey: productKey,
    providedValues: completeProduct,
  };
  const optionCompatibility = await productCatalogServiceClient.validateCompatibility(
    compatibilityRequest
  );
  const qtyCompatibility = await productCatalogServiceClient.getMerchandisableQuantities(
    productKey,
    completeProduct
  );

  if (!optionCompatibility) {
    Logger.instance.error(
      "Failed to add to cart. Incompatible selections from URL sel query parameter."
    );
    return;
  }
  if (quantity && !qtyCompatibility.includes(quantity)) {
    Logger.instance.error(
      "Failed to add to cart. Incompatible quantity from URL qty query parameter."
    );
    return;
  }

  const selectedQuantity = quantity || qtyCompatibility[0];

  await new CartService().addToCart(
    productKey,
    productName,
    version,
    productPageMerchandisingDetail,
    completeProduct,
    qtyCompatibility,
    selectedQuantity,
    imageUrl,
    locale,
    shopperId,
    authorizationHeader,
    vistacartTenant || ""
  );

  try {
    const siteflowInformation = await getAction({
      tenant,
      culture: locale,
      productKey: productKey,
      version,
      optionValues: selections,
      quantity,
      flowAction: "addtocart",
      optimizelyEndUserId: ensureUserTestId(),
      isMobile,
      mpvId,
    });

    TrackingUtil.trackCtaTiming(stopwatch.stop());
    if (siteflowInformation?.url) {
      TrackingUtil.trackAddToCart({
        pageSection,
        mpvId,
        selections,
        productKey,
        selectedQuantity,
        listPrice: prices[selectedQuantity].totalListPrice.untaxed,
        price: prices[selectedQuantity].totalDiscountedPrice.untaxed,
        version,
      });
      TrackingUtil.trackUpdatedSelections(
        mpvId,
        productName,
        productKey,
        selections,
        initialSelections,
        initialQuantity,
        selectedQuantity,
        prices,
        initialPrices,
        productBuilderOptions
      );

      window.location.assign(siteflowInformation?.url);
    } else {
      Logger.instance.error(
        "Siteflow failed to get addtocart response - Could not assign window location to add to cart url",
        {
          locale,
          mpvId,
          selections,
          quantity,
          requestUrl: siteflowInformation?.requestUrl,
        }
      );
    }
  } catch (ex) {
    Logger.instance.error("Unable to get cart URL", {
      locale,
      error: ex,
    });
    return;
  }
};

const getAddToCartImageUrl = (props: {
  heroGallery: (ContentfulCloudinaryImageComponent<CloudinaryImage> | CloudinaryVideoMedia)[];
  defaultMpvImageUrl?: string;
  selectedReferentialImage?: ContentfulCloudinaryImageComponent<CloudinaryImage>;
}) => {
  const { heroGallery, defaultMpvImageUrl, selectedReferentialImage } = props;
  const referentialImage = selectedReferentialImage;
  const referentialImageUrl =
    referentialImage?.cloudinaryImage?.mobileCloudinaryUrl ||
    referentialImage?.cloudinaryImage?.cloudinaryUrl;

  const addToCartImageUrl =
    referentialImageUrl || defaultMpvImageUrl || getDefaultImage(heroGallery) || "";

  return addToCartImageUrl;
};

export { addToCartOnClickHandler, addToCart, getAddToCartImageUrl };
